import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Webhook,
    RefreshCw,
    AlertTriangle,
    CheckCircle2,
    Clock,
    Ban,
    Skull,
    Search,
    X,
    Trash2,
    RotateCw,
    ChevronRight,
} from "lucide-react";

const STATUS_META = {
    queued: { label: "Queued", icon: Clock, tone: "text-amber-300 border-amber-300/30 bg-amber-300/5" },
    processing: { label: "Processing", icon: RefreshCw, tone: "text-sky-300 border-sky-300/30 bg-sky-300/5" },
    succeeded: { label: "Succeeded", icon: CheckCircle2, tone: "text-emerald-300 border-emerald-300/30 bg-emerald-300/5" },
    failed: { label: "Failed", icon: AlertTriangle, tone: "text-orange-300 border-orange-300/30 bg-orange-300/5" },
    dead: { label: "Dead", icon: Skull, tone: "text-red-300 border-red-300/40 bg-red-300/10" },
    cancelled: { label: "Cancelled", icon: Ban, tone: "text-white/40 border-white/15 bg-white/5" },
};

function fmt(ts) {
    if (!ts) return "—";
    try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
}

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
}

export default function WebhookQueuePage() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ status: "", target_url_contains: "", source: "" });
    const [detail, setDetail] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const q = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => v && q.set(k, v));
            q.set("limit", "200");
            const { data } = await api.get(`/admin/webhook-jobs?${q.toString()}`);
            setItems(data.items || []);
            setCounts(data.counts || {});
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    const apply = (e) => { e?.preventDefault(); load(); };
    const clear = () => { setFilters({ status: "", target_url_contains: "", source: "" }); setTimeout(load, 0); };

    const retry = async (id) => {
        try {
            await api.post(`/admin/webhook-jobs/${id}/retry`);
            toast.success("Re-queued for delivery");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };
    const cancel = async (id) => {
        try {
            await api.post(`/admin/webhook-jobs/${id}/cancel`);
            toast.success("Cancelled");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };
    const purge = async () => {
        if (!window.confirm("Purge all dead/cancelled jobs older than 30 days?")) return;
        try {
            const { data } = await api.post(`/admin/webhook-jobs/purge`, { older_than_days: 30 });
            toast.success(`Deleted ${data.deleted} jobs`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };
    const bulkRetry = async () => {
        if (!window.confirm("Re-queue ALL dead jobs visible in the current filter?")) return;
        try {
            const { data } = await api.post(`/admin/webhook-jobs/bulk-retry`, {
                target_url_contains: filters.target_url_contains || null,
                source: filters.source || null,
            });
            toast.success(`Re-queued ${data.requeued} of ${data.matched}`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    return (
        <div className="px-8 py-8" data-testid="webhook-queue-page">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED] flex items-center gap-2">
                        <Webhook className="w-3 h-3" /> Webhook Queue
                    </div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">Outbound delivery queue</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Every outbound webhook is persisted, retried with exponential backoff, and
                        forensic-logged here. Jobs go <span className="font-mono text-amber-300">queued</span> →{" "}
                        <span className="font-mono text-sky-300">processing</span> →{" "}
                        <span className="font-mono text-emerald-300">succeeded</span> or{" "}
                        <span className="font-mono text-red-300">dead</span> after exhausting retries.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={bulkRetry}
                        data-testid="wq-bulk-retry"
                        className="border border-[#7C3AED]/40 bg-[#7C3AED]/10 text-white hover:bg-[#7C3AED]/20 px-3 py-2 rounded-md text-xs flex items-center gap-1.5"
                    >
                        <RotateCw className="w-3.5 h-3.5" /> Bulk retry dead
                    </button>
                    <button
                        onClick={purge}
                        data-testid="wq-purge"
                        className="border border-red-500/30 bg-red-500/5 text-red-200 hover:bg-red-500/10 px-3 py-2 rounded-md text-xs flex items-center gap-1.5"
                    >
                        <Trash2 className="w-3.5 h-3.5" /> Purge old
                    </button>
                    <button
                        onClick={load}
                        data-testid="wq-refresh"
                        className="border border-white/15 px-3 py-2 rounded-md text-sm hover:bg-white/5 flex items-center gap-1.5"
                    >
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                </div>
            </div>

            {/* Status counter cards */}
            <div className="grid grid-cols-6 gap-2 mb-5">
                {(["queued", "processing", "succeeded", "failed", "dead", "cancelled"]).map((s) => {
                    const meta = STATUS_META[s];
                    const Icon = meta.icon;
                    const active = filters.status === s;
                    return (
                        <button
                            key={s}
                            data-testid={`wq-kpi-${s}`}
                            onClick={() => { setFilters((p) => ({ ...p, status: active ? "" : s })); setTimeout(load, 0); }}
                            className={`bg-[#0a0a0a] border rounded-md p-3 text-left transition-colors ${active ? "border-white/40" : "border-white/10 hover:bg-white/5"}`}
                        >
                            <div className="flex items-center gap-1.5">
                                <Icon className={`w-3.5 h-3.5 ${meta.tone.split(" ")[0]}`} strokeWidth={1.6} />
                                <span className="label-xs">{meta.label}</span>
                            </div>
                            <div className="font-display text-2xl mt-1">{counts[s] || 0}</div>
                        </button>
                    );
                })}
            </div>

            {/* Filters */}
            <form onSubmit={apply} className="bg-[#0a0a0a] border border-white/10 rounded-md p-4 mb-5">
                <div className="grid grid-cols-3 gap-3">
                    <FilterInput label="Target URL contains" value={filters.target_url_contains} onChange={(v) => setFilters((p) => ({ ...p, target_url_contains: v }))} testId="wq-filter-url" placeholder="example.com/webhook" />
                    <FilterInput label="Source" value={filters.source} onChange={(v) => setFilters((p) => ({ ...p, source: v }))} testId="wq-filter-source" placeholder="flow_run" />
                    <div className="flex items-end gap-2 justify-end">
                        <button type="button" onClick={clear} data-testid="wq-clear" className="px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">Clear</button>
                        <button type="submit" data-testid="wq-apply" className="px-4 py-2 text-sm bg-[#7C3AED] text-white rounded-md flex items-center gap-2">
                            <Search className="w-4 h-4" /> Apply
                        </button>
                    </div>
                </div>
            </form>

            {/* Job table */}
            <div className="bg-[#0a0a0a] border border-white/10 rounded-md overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="text-left">
                            <th className="label-xs px-5 py-3">Created</th>
                            <th className="label-xs px-5 py-3">Status</th>
                            <th className="label-xs px-5 py-3">Target URL</th>
                            <th className="label-xs px-5 py-3">Tenant</th>
                            <th className="label-xs px-5 py-3 text-center">Attempts</th>
                            <th className="label-xs px-5 py-3">Next try</th>
                            <th className="label-xs px-5 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="px-5 py-8 text-center text-xs text-white/40 font-mono">Loading…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={7} className="px-5 py-16 text-center">
                                <Webhook className="w-10 h-10 text-white/20 mx-auto mb-3" strokeWidth={1.2} />
                                <div className="text-sm text-white/60">No webhook jobs match these filters</div>
                            </td></tr>
                        ) : items.map((j, idx) => {
                            const meta = STATUS_META[j.status] || STATUS_META.queued;
                            const Icon = meta.icon;
                            const canRetry = ["dead", "failed", "cancelled"].includes(j.status);
                            const canCancel = !["succeeded", "cancelled"].includes(j.status);
                            return (
                                <tr key={j.id} data-testid={`wq-row-${idx}`} className="data-row border-t border-white/5 hover:bg-white/[0.04]">
                                    <td className="px-5 py-3 font-mono text-xs text-white/60 whitespace-nowrap">{fmt(j.created_at)}</td>
                                    <td className="px-5 py-3">
                                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${meta.tone}`}>
                                            <Icon className="w-3 h-3" strokeWidth={2} />
                                            {meta.label}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3 text-xs font-mono text-white/85 truncate max-w-[260px]" title={j.target_url}>{j.target_url}</td>
                                    <td className="px-5 py-3 text-xs font-mono text-white/60 truncate max-w-[120px]">{j.tenant_id || "—"}</td>
                                    <td className="px-5 py-3 text-center text-xs font-mono text-white/85">
                                        {j.attempt_count}/{j.max_attempts}
                                        {j.last_status_code && <span className="text-white/40"> · {j.last_status_code}</span>}
                                    </td>
                                    <td className="px-5 py-3 text-xs font-mono text-white/60">{fmt(j.next_attempt_at)}</td>
                                    <td className="px-5 py-3 text-right">
                                        <div className="flex justify-end gap-1">
                                            {canRetry && (
                                                <button
                                                    onClick={() => retry(j.id)}
                                                    data-testid={`wq-retry-${idx}`}
                                                    title="Retry now"
                                                    className="p-1.5 rounded hover:bg-[#7C3AED]/20 text-[#c7a6ff]"
                                                >
                                                    <RotateCw className="w-3.5 h-3.5" strokeWidth={1.6} />
                                                </button>
                                            )}
                                            {canCancel && (
                                                <button
                                                    onClick={() => cancel(j.id)}
                                                    data-testid={`wq-cancel-${idx}`}
                                                    title="Cancel"
                                                    className="p-1.5 rounded hover:bg-red-500/20 text-red-300"
                                                >
                                                    <Ban className="w-3.5 h-3.5" strokeWidth={1.6} />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => setDetail(j.id)}
                                                data-testid={`wq-detail-${idx}`}
                                                title="View attempt log"
                                                className="p-1.5 rounded hover:bg-white/10 text-white/70"
                                            >
                                                <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.6} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {detail && <DetailModal jobId={detail} onClose={() => setDetail(null)} onRefresh={load} />}
        </div>
    );
}

function FilterInput({ label, value, onChange, testId, placeholder }) {
    return (
        <div>
            <div className="label-xs mb-1.5">{label}</div>
            <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || ""} data-testid={testId}
                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30" />
        </div>
    );
}

function DetailModal({ jobId, onClose, onRefresh }) {
    const [job, setJob] = useState(null);
    const [error, setError] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get(`/admin/webhook-jobs/${jobId}`);
            setJob(data);
        } catch (e) {
            setError(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };
    useEffect(() => { load(); }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden bg-[#0a0a0a] border border-white/15 rounded-md flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="wq-detail-modal">
                <div className="px-5 py-4 border-b border-white/10 flex items-center">
                    <div className="min-w-0">
                        <div className="font-display text-lg tracking-tight">Webhook job</div>
                        <div className="text-[11px] text-white/50 font-mono truncate">{jobId}</div>
                    </div>
                    <button onClick={onClose} className="ml-auto text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="flex-1 overflow-auto p-5 space-y-4">
                    {error && <div className="text-xs text-red-300 font-mono">{error}</div>}
                    {!job && !error && <div className="text-xs font-mono text-white/40">Loading…</div>}
                    {job && (
                        <>
                            <div className="grid grid-cols-2 gap-3">
                                <KV label="Status" value={(STATUS_META[job.status] || {}).label || job.status} />
                                <KV label="Tenant" value={job.tenant_id || "—"} />
                                <KV label="Target" value={job.target_url} mono />
                                <KV label="Source" value={`${job.source} · ${job.source_id || "—"}`} mono />
                                <KV label="Attempts" value={`${job.attempt_count}/${job.max_attempts}`} />
                                <KV label="Last status" value={job.last_status_code || "—"} mono />
                                <KV label="Next try" value={fmt(job.next_attempt_at)} mono />
                                <KV label="Created" value={fmt(job.created_at)} mono />
                            </div>
                            <div>
                                <div className="label-xs mb-2">Attempt log ({(job.attempts || []).length})</div>
                                <div className="space-y-2">
                                    {(job.attempts || []).slice().reverse().map((a, i) => (
                                        <div key={`${a.attempt_no}-${i}`} className="border border-white/10 rounded-md p-3" data-testid={`wq-attempt-${a.attempt_no}`}>
                                            <div className="flex items-center justify-between text-xs">
                                                <span className="font-mono text-white/85">
                                                    Attempt #{a.attempt_no} · {fmt(a.attempted_at)}
                                                </span>
                                                <span className={`font-mono text-[10px] uppercase tracking-wider ${a.outcome === "succeeded" ? "text-emerald-300" : "text-red-300"}`}>
                                                    {a.outcome || "—"}
                                                </span>
                                            </div>
                                            <div className="text-[11px] font-mono text-white/55 mt-1">
                                                status={a.status_code ?? "—"} · latency={a.latency_ms ?? "—"}ms · signed={String(!!a.signed)}
                                            </div>
                                            {a.error && (
                                                <div className="text-[11px] text-red-300 font-mono mt-1.5 break-all whitespace-pre-wrap">
                                                    {a.error}
                                                </div>
                                            )}
                                            {a.response_body_preview && (
                                                <details className="mt-1.5">
                                                    <summary className="text-[10px] uppercase tracking-wider text-white/40 cursor-pointer">Response body</summary>
                                                    <pre className="text-[11px] font-mono whitespace-pre-wrap bg-[#050505] border border-white/10 rounded p-2 mt-1 max-h-40 overflow-auto">{a.response_body_preview}</pre>
                                                </details>
                                            )}
                                        </div>
                                    ))}
                                    {!(job.attempts || []).length && (
                                        <div className="text-xs font-mono text-white/40 text-center py-6">No attempts yet</div>
                                    )}
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
                                {["dead", "failed", "cancelled"].includes(job.status) && (
                                    <button
                                        onClick={async () => { await api.post(`/admin/webhook-jobs/${jobId}/retry`); toast.success("Re-queued"); load(); onRefresh(); }}
                                        data-testid="wq-detail-retry"
                                        className="px-4 py-2 text-sm bg-[#7C3AED] text-white rounded-md hover:bg-[#7C3AED]/90"
                                    >
                                        Retry now
                                    </button>
                                )}
                                {!["succeeded", "cancelled"].includes(job.status) && (
                                    <button
                                        onClick={async () => { await api.post(`/admin/webhook-jobs/${jobId}/cancel`); toast.success("Cancelled"); load(); onRefresh(); }}
                                        data-testid="wq-detail-cancel"
                                        className="px-4 py-2 text-sm border border-red-500/40 text-red-200 hover:bg-red-500/10 rounded-md"
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function KV({ label, value, mono }) {
    return (
        <div>
            <div className="label-xs">{label}</div>
            <div className={`text-sm mt-1 ${mono ? "font-mono text-white/85 break-all" : "text-white"}`}>{value}</div>
        </div>
    );
}
